
/**
 * Module dependencies.
 */

import 'react-phone-input-2/lib/high-res.css';
import { FieldAlert } from '../../forms/field-alert';
import { FieldError } from '../../forms/field-error';
import { FormGroup, Label, textDesktopStyle } from '../../forms/fields-styled-components';
import { ForwardedRef, ReactNode, forwardRef, useCallback, useState } from 'react';
import { color, media } from 'react-components/styles';
import { ifProp, theme } from 'styled-tools';
import { useField, useFormState } from '@seegno/react-forms';
import PhoneNumberInput from 'react-phone-input-2';
import caretIcon24 from '../../assets/svg/24/caret.svg';
import styled, { css } from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  autoFocus?: boolean;
  className?: string;
  defaultCountry?: string;
  disabled?: boolean;
  helpText?: ReactNode;
  id?: string;
  label: string;
  name: string;
  readOnly?: string;
};

/**
 * Default country code.
 */

const defaultCountryCode = 'us';

/**
 * Excluded countries.
 */

const excludedCountries = ['cu', 'ir', 'kp', 'sy', 'ss'];

/**
 * `StyledFormGroup` styled component.
 */

const StyledFormGroup = styled(FormGroup)<{ disabled: boolean; hasError: boolean; hasFocus: boolean }>`
  .react-tel-input {
    background-color: ${color('gray300')};
    border: 2px solid ${color('gray300')};
    border-radius: ${theme('dimensions.borderRadius')}px;
    height: 50px;
    transition: ${theme('animations.defaultTransition')};
    transition-property: background-color, border-color;

    &:hover {
      ${ifProp({ hasError: false, hasFocus: false }, css`
        border-color: ${color('gray400')};
      `)}
    }

    ${ifProp('hasFocus', css`
      border-color: ${color('primary')};
    `)}

    ${ifProp('hasError', css`
      border-color: ${color('danger')};
    `)}

    ${ifProp('disabled', css`
      background-color: ${color('gray100')};
      border-color: ${color('gray100')};
    `)}

    ${media.min('md')`
      height: 54px;
    `}

    .form-control {
      ${textDesktopStyle}

      background: transparent;
      border: none;
      color: ${color('secondary')};
      height: 100%;
      outline: none;
      padding: 20px 14px 6px 70px;
      transition: color ${theme('animations.defaultTransition')};
      width: 100%;

      ${ifProp('disabled', css`
        color: ${color('gray400')};
      `)}

      ${media.min('md')`
        padding-top: 20px;
        padding-right: 10px;
      `}

      &.open {
        z-index: ${props => theme('zIndex.dropdown.menu')(props) + 1};
      }
    }

    .selected-flag {
      padding-left: 10px;

      ${media.min('md')`
        padding-left: 14px;
      `}

      .arrow {
        background: url('data:image/svg+xml;utf8,${caretIcon24}');
        border: none;
        height: 16px;
        transform: translate(-3px, -4px);
        width: 16px;

        &.up {
          border: none;
          transform: translate(-3px, -4px) rotate(180deg);
        }
      }

      &:hover,
      &:focus {
        background: transparent;
      }
    }

    .flag-dropdown {
      background-color: unset;
      border: none;
      height: 100%;
      padding-bottom: 6px;
      padding-top: 20px;
      transition: opacity ${theme('animations.defaultTransition')};

      &.open {
        width: 100%;
        z-index: ${theme('zIndex.dropdown.menu')};
      }

      &.open,
      &.open .selected-flag,
      &:hover,
      &:focus {
        background: transparent;
      }

      ${ifProp('disabled', css`
        opacity: 0.3;
      `)}
    }

    .country-list {
      border-radius: ${theme('dimensions.borderRadius')}px;
      box-shadow: ${theme('boxShadow.dropdownMenu')};
      width: inherit;

      .flag {
        left: 20px;
        margin-top: -3px;
        top: 8px;
      }

      .divider {
        border-color: ${color('gray200')};
      }

      .country {
        outline: 0;
        padding: 8px 0 8px 50px;
        position: relative;

        .dial-code {
          color: ${color('secondary')};
        }

        &.highlight,
        &:hover {
          background-color: ${color('blue200')};
        }
      }
    }
  }
`;

/**
 * `StyledLabel` styled component.
 */

const StyledLabel = styled(Label)<{ isFloating: boolean }>`
  z-index: 1;

  ${ifProp('isFloating', css`
    transform: translateY(-6px);
  `)}
`;

/**
 * `PhoneInput` component.
 */

function PhoneInput(props: Props, ref: ForwardedRef<HTMLInputElement>): JSX.Element {
  const { className, defaultCountry = defaultCountryCode, disabled, label, helpText, id, name, ...rest } = props;
  const { error, meta: { active }, onChange, value, ...fieldProps } = useField(name);
  const { alreadySubmitted, isSubmitting } = useFormState();
  const [dialCode, setDialCode] = useState();
  const hasError = !!error && alreadySubmitted;
  const isDisabled = disabled || isSubmitting;
  const handleOnChange = useCallback((value: any, data: any) => {
    const dialCode = data?.dialCode;
    const hasPhoneValue = !!value && dialCode !== value;
    const phoneValue = hasPhoneValue ? `+${value}` : undefined;

    setDialCode(dialCode);
    onChange(phoneValue);
  }, [onChange]);

  return (
    <StyledFormGroup
      className={className}
      disabled={isDisabled}
      hasError={hasError}
      hasFocus={active}
    >
      <StyledLabel
        disabled={isDisabled}
        htmlFor={id ?? name}
        isFloating
      >
        {label}
      </StyledLabel>

      <PhoneNumberInput
        {...fieldProps}
        country={defaultCountry}
        countryCodeEditable={!(value || active)}
        disabled={disabled}
        excludeCountries={excludedCountries}
        inputProps={{
          ...rest,
          id: id ?? name,
          name
        }}
        onChange={handleOnChange}
        placeholder={null}
        preferredCountries={[defaultCountry]}
        ref={ref}
        specialLabel={null}
        value={value || dialCode}
      />

      {helpText && (
        <FieldAlert isOpen={!hasError}>
          {helpText}
        </FieldAlert>
      )}

      <FieldError
        error={error}
        isOpen={hasError}
        name={name}
      />
    </StyledFormGroup>
  );
}

/**
 * Forwarded `PhoneInput`.
 */

const ForwardedInput = forwardRef<HTMLInputElement, Props>(PhoneInput);

/**
 * Export `PhoneInput` component.
 */

export { ForwardedInput as PhoneInput };
